<template>
  <div class="grid grid-cols-1 md:grid-cols-2 sm:gap-10 sm:gap-6">
    <!-- Grid del calendario -->
    <div class="card-fbochas mb-10 p-4 sm:p-6 sm:mb-0 lg:p-10">
      <div class="flex items-center justify-between mb-4 sm:mb-6 gap-2">
        <div class="min-w-0">
          <h3
            class="text-xl sm:text-2xl font-bold text-white capitalize truncate"
          >
            {{ monthLabel }}
          </h3>
          <button
            v-if="!isCurrentMonth"
            @click="goToday"
            class="text-xs text-bochas-rojo hover:underline mt-0.5 inline-flex items-center gap-1"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="9" />
            </svg>
            Ir al mes en curso ({{ currentMonthLabel }})
          </button>
        </div>
        <div class="flex gap-1 sm:gap-2 flex-shrink-0">
          <button
            @click="prevMonth"
            class="w-8 h-8 sm:w-9 sm:h-9 rounded-full hover:bg-white/10 flex items-center justify-center text-white"
            aria-label="Mes anterior"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-4 h-4 sm:w-5 sm:h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <button
            @click="nextMonth"
            class="w-8 h-8 sm:w-9 sm:h-9 rounded-full hover:bg-white/10 flex items-center justify-center text-white"
            aria-label="Mes siguiente"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-4 h-4 sm:w-5 sm:h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      <div
        class="grid grid-cols-7 gap-1 sm:gap-2 text-xs sm:text-base text-center mb-2"
      >
        <div
          v-for="d in dayHeaders"
          :key="d"
          class="text-gray-400 py-1 sm:py-2 font-medium"
        >
          {{ d }}
        </div>
      </div>

      <div class="grid grid-cols-7 gap-1 sm:gap-2 text-sm sm:text-base">
        <template v-for="(cell, idx) in calendarCells" :key="idx">
          <div v-if="!cell.day" class="py-2"></div>
          <button
            v-else
            type="button"
            @click="selectDay(cell.day)"
            :class="[
              dayClass(cell),
              isToday(cell.day)
                ? 'ring-2 ring-bochas-rojo'
                : selectedDay === cell.day
                ? 'ring-2 ring-white ring-offset-2 ring-offset-bochas-bg'
                : '',
            ]"
          >
            {{ cell.day }}
          </button>
        </template>
      </div>
    </div>

    <!-- Lista de eventos del mes -->
    <div class="min-w-0 md:relative md:min-h-0">
      <div
        ref="eventsScrollRef"
        class="calendario-events-scroll space-y-5 pr-1 sm:pr-2 md:absolute md:inset-0 md:overflow-y-auto"
        :class="eventsListScrollClass"
        @scroll="updateScrollHint"
      >
      <div
        v-if="eventosFiltrados.length === 0"
        class="text-center text-gray-400 py-8 text-sm"
      >
        {{
          selectedDay !== null
            ? "No hay eventos este día."
            : "No hay eventos próximos."
        }}
      </div>

      <template v-for="group in gruposParaMostrar" :key="group.id ?? group._id">
        <div
          v-if="group._isDivider"
          class="text-xs text-gray-400 uppercase tracking-wide pt-2 mt-2 border-t border-white/10 font-semibold"
        >
          {{ group._label }}
        </div>
        <div v-else class="space-y-5" :class="dayGroupScrollClass(group)">
          <article
            v-for="evento in group.items"
            :key="evento._id ?? evento.id"
            role="button"
            tabindex="0"
            @click="openModal(evento)"
            @keydown.enter.prevent="openModal(evento)"
            @keydown.space.prevent="openModal(evento)"
            :class="[
              'card-fbochas relative flex items-start gap-3 sm:gap-4 sm:items-center',
              compact ? 'p-3 sm:p-5' : 'p-3 sm:p-4',
              'cursor-pointer',
            ]"
          >
            <div
              :class="[
                'flex-shrink-0 w-16 h-16 rounded-2xl flex flex-col items-center justify-center',
                'bg-degradado-bochas',
                compact ? 'p-3' : 'p-2 sm:p-4',
                compact ? '' : 'min-w-[56px]',
              ]"
            >
              <span
                :class="[
                  'text-white font-bold leading-none',
                  compact ? 'text-2xl' : 'text-xl sm:text-2xl',
                ]"
              >
                {{ dayNum(evento) }}
              </span>
              <span
                :class="[
                  'text-white uppercase mt-1',
                  compact
                    ? 'text-[12px] font-normal'
                    : 'text-[10px] sm:text-xs',
                ]"
              >
                {{ monthShort(evento) }}
              </span>
            </div>
            <div class="flex-1 h-100 min-w-0">
              <p
                :class="[
                  'mb-1 text-white',
                  compact
                    ? 'calendar-event-title font-normal text-base sm:text-lg mb-2 sm:mb-2'
                    : 'font-semibold text-sm sm:text-base',
                ]"
              >
                {{ evento.title }}
              </p>
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0 flex-1 space-y-1 overflow-hidden">
                  <div class="flex items-center justify-between gap-4">
                    <p
                      v-if="evento.organizerClub"
                      :class="[
                        'flex min-w-0 flex-1 items-center gap-1 text-white',
                        compact ? 'text-xs' : 'text-xs',
                      ]"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span class="truncate">{{ evento.organizerClub }}</span>
                    </p>
                    <p
                      v-if="showTime(evento)"
                      :class="[
                        'flex flex-shrink-0 items-center gap-1 text-white',
                        compact ? 'text-xs' : 'text-xs',
                      ]"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      {{ formatTime(evento.appointmentTime) }} hs
                    </p>
                  </div>
                  <p
                    v-if="evento.association"
                    :class="[
                      'flex min-w-0 items-center gap-1 text-white',
                      compact ? 'text-xs' : 'text-xs',
                    ]"
                  >
                    <IconFbochas
                      class="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0"
                      name="categories"
                    />
                    <span class="truncate">{{ evento.association }}</span>
                  </p>
                </div>
                <button
                  type="button"
                  @click.stop="openModal(evento)"
                  :class="[
                    'flex flex-shrink-0 items-center gap-1 self-end text-white sm:text-gray-400 sm:hover:text-white',
                    compact ? 'text-xs' : 'text-[11px] sm:text-xs',
                  ]"
                >
                  Más info
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-3 h-3 sm:w-4 sm:h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </article>
        </div>
      </template>
      </div>
      <div
        v-if="showScrollHint"
        class="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center bg-gradient-to-t from-bochas-bg via-bochas-bg/90 to-transparent pb-2 pt-10 md:hidden"
      >
        <span class="rounded-full bg-white/10 px-3 py-1 text-xs text-white">
          Deslizá para ver más ↓
        </span>
      </div>
    </div>
  </div>

  <!-- Modal compartido -->
  <Teleport to="body">
    <Transition name="bottom-sheet-modal">
      <div
        v-if="modalEvento"
        @click="closeModal"
        class="mobile-modal-overlay fixed inset-0 z-50 flex justify-center bg-black/80 sm:items-center sm:p-4 lg:p-8"
      >
        <div
          v-bottom-sheet="closeModal"
          @click.stop
          class="mobile-bottom-sheet vue-managed-sheet w-full max-w-2xl min-h-[60vh] max-h-[92svh] overflow-y-auto rounded-t-2xl border border-white/100 bg-bochas-bg p-4 sm:min-h-0 sm:rounded-2xl sm:p-6 sm:max-h-[88vh] lg:max-w-4xl lg:p-8 xl:max-w-5xl xl:p-10 2xl:max-w-6xl 2xl:p-12"
        >
          <div
            class="relative mb-4 flex items-start justify-between gap-3 border-b border-white/50 pb-4 pr-8 sm:pr-0 lg:mb-7 lg:gap-6 lg:pb-7"
          >
            <div class="flex min-w-0 items-start gap-3">
              <div
                class="flex-shrink-0 p-2 sm:p-3 lg:p-5 rounded-2xl bg-degradado-bochas flex flex-col items-center justify-center min-w-[64px] lg:w-20"
              >
                <span
                  class="text-white text-xl sm:text-2xl lg:text-4xl font-bold leading-none"
                  >{{ modalDayNum }}</span
                >
                <span
                  class="text-white text-[10px] sm:text-xs uppercase mt-1"
                  >{{ modalMonthShort }}</span
                >
              </div>
              <div class="min-w-0 flex-1">
                <h3
                  class="text-base font-normal leading-tight text-white sm:text-lg lg:text-2xl 2xl:text-3xl"
                >
                  {{ modalEvento.title }}
                </h3>
                <div class="mt-3 flex items-start justify-between gap-3 sm:mt-4">
                  <div class="min-w-0 flex-1 space-y-1">
                    <div class="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                      <p
                        v-if="modalEvento.organizerClub"
                        class="flex min-w-0 flex-1 items-center gap-1 text-sm font-normal text-white sm:text-base lg:text-lg"
                      >
                        <IconFbochas
                          class="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0"
                          name="location"
                        />
                        <span>{{
                          modalEvento.organizerClub
                        }}</span>
                      </p>
                      <p
                        v-if="showTime(modalEvento)"
                        class="flex flex-shrink-0 items-center gap-1 text-sm font-normal text-white sm:text-base lg:text-lg"
                      >
                        <IconFbochas
                          class="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0"
                          name="reloj"
                        />
                        {{ formatTime(modalEvento.appointmentTime) }} hs
                      </p>
                    </div>
                    <p
                      v-if="modalEvento.association"
                      class="flex min-w-0 items-center gap-1 text-sm font-normal text-white sm:text-base lg:text-lg"
                    >
                      <IconFbochas
                        class="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0"
                        name="categories"
                      />
                      <span>{{ modalEvento.association }}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <button
              @click="closeModal"
              class="absolute right-0 top-0 text-2xl text-gray-400 hover:text-white sm:static sm:flex-shrink-0"
              aria-label="Cerrar"
            >
              ✕
            </button>
          </div>

          <div
            class="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-x-10 lg:gap-y-8 text-sm lg:text-base"
          >
            <div v-if="modalEvento.sorteoDate" class="flex items-start gap-3">
              <IconFbochas
                name="calendarClock"
                :size="28"
                class="text-bochas-rojo flex-shrink-0 mt-0.5"
              />
              <div>
                <p class="text-lg uppercase tracking-wide text-white mb-0.5">
                  Sorteo
                </p>
                <p class="text-white">
                  {{ formatDate(modalEvento.sorteoDate)
                  }}<span
                    v-if="modalEvento.sorteoTime"
                    class="text-sm font-normal"
                    >, {{ formatTime(modalEvento.sorteoTime) }} hs</span
                  >
                </p>
              </div>
            </div>

            <div v-if="modalEvento.address" class="flex items-start gap-3">
              <IconFbochas
                name="location"
                :size="28"
                class="text-bochas-rojo flex-shrink-0 mt-0.5"
              />
              <div>
                <p class="text-lg uppercase tracking-wide text-white mb-0.5">
                  Ubicación
                </p>
                <p class="text-white">
                  <a
                    v-if="modalEvento.location"
                    :href="`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      modalEvento.address + ', ' + modalEvento.location,
                    )}`"
                    target="_blank"
                    rel="noopener"
                    class="hover:underline"
                  >
                    {{ modalEvento.address
                    }}<span v-if="modalEvento.location"
                      >, {{ modalEvento.location }}</span
                    >
                  </a>
                  <span v-else class="text-sm font-normal">{{
                    modalEvento.address
                  }}</span>
                </p>
              </div>
            </div>

            <div
              v-if="modalEvento.categories && modalEvento.categories.length > 0"
              class="flex items-start gap-3"
            >
              <IconFbochas
                name="categories"
                :size="28"
                class="text-bochas-rojo flex-shrink-0 mt-0.5"
              />
              <div>
                <p class="text-lg uppercase tracking-wide text-white mb-0.5">
                  Categorías
                </p>
                <ul class="space-y-0.5">
                  <li
                    v-for="(c, idx) in modalEvento.categories"
                    :key="idx"
                    class="text-white text-sm font-normal"
                  >
                    <span class="text-bochas-rojo text-sm font-normal">•</span>
                    {{ c }}
                  </li>
                </ul>
              </div>
            </div>

            <div
              v-if="modalEvento.contacts && modalEvento.contacts.length > 0"
              class="flex items-start gap-3"
            >
              <IconFbochas
                name="phoneCall"
                :size="28"
                class="text-bochas-rojo flex-shrink-0 mt-0.5"
              />
              <div>
                <p class="text-lg uppercase tracking-wide text-white mb-0.5">
                  Contacto
                </p>
                <div class="space-y-1">
                  <p
                    v-for="(c, idx) in modalEvento.contacts"
                    :key="idx"
                    class="text-white"
                  >
                    <span v-if="c.phone" class="text-sm font-normal"
                      >{{ c.phone
                      }}<span v-if="c.name"> ({{ c.name }})</span></span
                    >
                    <span v-else-if="c.name" class="text-sm font-normal">{{
                      c.name
                    }}</span>
                  </p>
                </div>
              </div>
            </div>

            <div
              v-if="modalEvento.additionalInfo"
              class="sm:col-span-2 flex items-start gap-3"
            >
              <IconFbochas
                name="file"
                :size="28"
                class="text-bochas-rojo flex-shrink-0 mt-0.5"
              />
              <div>
                <p class="text-lg uppercase tracking-wide text-white mb-0.5">
                  Información adicional
                </p>
                <RichTextContent
                  :html="modalEvento.additionalInfo"
                  class="text-white text-sm font-normal"
                />
              </div>
            </div>

          </div>

          <div
            v-if="modalEvento.reglamento"
            class="mt-6 pt-4 border-t border-white/10 flex justify-center"
          >
            <a
              :href="modalEvento.reglamento.url"
              target="_blank"
              rel="noopener"
              class="btn-bochas-azul text-sm inline-flex items-center gap-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Ver reglamento
            </a>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, nextTick, watch } from "vue";
import { useRoute } from "vue-router";
import { useListingState } from "@/composables/useListingState";
import { formatDate } from "@/utils/date";
import IconFbochas from "../Icons/IconFbochas.vue";
import RichTextContent from "@/components/Common/RichTextContent.vue";

const props = defineProps({
  events: { type: Array, required: true },
  compact: { type: Boolean, default: false }, // true = home, false = /calendario
});

const today = new Date();
const route = useRoute();
const { viewYear, viewMonth, selectedDay } = useListingState(`calendario:${route.path}`, {
  viewYear: today.getFullYear(),
  viewMonth: today.getMonth(),
  selectedDay: null,
});
const modalEvento = ref(null);
const eventsScrollRef = ref(null);
const showScrollHint = ref(false);

const dayHeaders = ["DO", "LU", "MA", "MI", "JU", "VI", "SA"];

const isCurrentMonth = computed(
  () =>
    viewYear.value === today.getFullYear() &&
    viewMonth.value === today.getMonth(),
);

const currentMonthLabel = computed(() =>
  today.toLocaleDateString("es-AR", { month: "long", year: "numeric" }),
);

const monthLabel = computed(() =>
  new Date(viewYear.value, viewMonth.value, 1).toLocaleDateString("es-AR", {
    month: "long",
    year: "numeric",
  }),
);

function parseLocalDate(value) {
  if (!value) return null;
  const dateOnly = String(value).split("T")[0];
  const [year, month, day] = dateOnly.split("-").map((n) => parseInt(n, 10));
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function eventDateKey(evento) {
  return String(evento.appointmentDay || "").split("T")[0];
}

function dateGroupCount(events) {
  return new Set(events.map((ev) => ev._dateKey || eventDateKey(ev))).size;
}

const eventosDelAnyo = computed(() =>
  props.events.filter((e) => {
    const ed = parseLocalDate(e.appointmentDay);
    if (!ed) return false;
    return (
      ed.getFullYear() === viewYear.value && ed.getMonth() === viewMonth.value
    );
  }),
);

const eventosDelMes = computed(() =>
  eventosDelAnyo.value.slice().sort((a, b) => {
    const da = parseLocalDate(a.appointmentDay)?.getTime() || 0;
    const db = parseLocalDate(b.appointmentDay)?.getTime() || 0;
    if (da !== db) return da - db;
    return (a.appointmentTime || "").localeCompare(b.appointmentTime || "");
  }),
);

// Mes actual + mes siguiente (para la lista). La grilla del calendario
// sigue usando `eventosDelAnyo` (solo mes actual), pero la lista de la
// derecha muestra ambos meses con un divider "Próximo mes" entre ellos.
const eventosDelMesYProximo = computed(() => {
  const currentYear = viewYear.value;
  const currentMonth = viewMonth.value;
  const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
  const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;

  return props.events
    .filter((e) => {
      const ed = parseLocalDate(e.appointmentDay);
      if (!ed) return false;
      const isCurrent =
        ed.getFullYear() === currentYear && ed.getMonth() === currentMonth;
      const isNext =
        ed.getFullYear() === nextYear && ed.getMonth() === nextMonth;
      return isCurrent || isNext;
    })
    .sort((a, b) => {
      const da = parseLocalDate(a.appointmentDay)?.getTime() || 0;
      const db = parseLocalDate(b.appointmentDay)?.getTime() || 0;
      if (da !== db) return da - db;
      return (a.appointmentTime || "").localeCompare(b.appointmentTime || "");
    })
    .map((e) => ({
      ...e,
      _day: dayNum(e),
      _month: monthShort(e),
      _dateKey: eventDateKey(e),
      _isNextMonth:
        parseLocalDate(e.appointmentDay)?.getMonth() === nextMonth &&
        parseLocalDate(e.appointmentDay)?.getFullYear() === nextYear,
    }));
});

const eventosDelFinDeSemana = computed(() => {
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const daysUntilFriday = todayStart.getDay() === 0 ? -2 : 5 - todayStart.getDay();
  const weekendStart = new Date(todayStart);
  weekendStart.setDate(todayStart.getDate() + daysUntilFriday);
  const weekendEnd = new Date(weekendStart);
  weekendEnd.setDate(weekendStart.getDate() + 2);

  return props.events
    .filter((event) => {
      const date = parseLocalDate(event.appointmentDay);
      return date && date >= todayStart && date >= weekendStart && date <= weekendEnd;
    })
    .sort((a, b) => {
      const dateA = parseLocalDate(a.appointmentDay)?.getTime() || 0;
      const dateB = parseLocalDate(b.appointmentDay)?.getTime() || 0;
      if (dateA !== dateB) return dateA - dateB;
      return (a.appointmentTime || "").localeCompare(b.appointmentTime || "");
    })
    .map((event) => ({
      ...event,
      _day: dayNum(event),
      _month: monthShort(event),
      _dateKey: eventDateKey(event),
      _isNextMonth: false,
    }));
});

const eventosFiltrados = computed(() => {
  if (selectedDay.value === null) return eventosDelFinDeSemana.value;
  // Cuándo hay un día seleccionado, filtrar SOLO del mes actual
  // (no queremos traer el día 15 de agosto si estamos en julio y tocamos el 15).
  return eventosList.value.filter(
    (e) => !e._isNextMonth && e._day === selectedDay.value,
  );
});

const gruposParaMostrar = computed(() => {
  const groups = [];
  const groupsByDate = new Map();
  let nextMonthDividerAdded = false;

  for (const ev of eventosFiltrados.value) {
    if (ev._isNextMonth && !nextMonthDividerAdded) {
      groups.push({
        _isDivider: true,
        _label: "Próximo mes",
        _id: "divider-next-month",
      });
      groupsByDate.clear();
      nextMonthDividerAdded = true;
    }

    const dateKey = ev._dateKey || eventDateKey(ev);
    if (!groupsByDate.has(dateKey)) {
      const group = {
        id: `group-${dateKey}`,
        dateKey,
        items: [],
      };
      groupsByDate.set(dateKey, group);
      groups.push(group);
    }
    groupsByDate.get(dateKey).items.push(ev);
  }

  return groups;
});

const eventsListScrollClass = computed(() => {
  return eventosFiltrados.value.length > 3
    ? "max-h-[380px] overflow-y-auto md:max-h-none"
    : "";
});

function updateScrollHint() {
  const element = eventsScrollRef.value;
  showScrollHint.value = Boolean(
    element &&
      element.scrollHeight > element.clientHeight + 2 &&
      element.scrollTop + element.clientHeight < element.scrollHeight - 2,
  );
}

watch(eventosFiltrados, async () => {
  await nextTick();
  updateScrollHint();
}, { immediate: true });

function dayGroupScrollClass() {
  return "";
}

const calendarCells = computed(() => {
  const firstDay = new Date(viewYear.value, viewMonth.value, 1);
  const startWeekday = firstDay.getDay();
  const lastDay = new Date(viewYear.value, viewMonth.value + 1, 0);
  const totalDays = lastDay.getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push({ day: null });
  for (let d = 1; d <= totalDays; d++) cells.push({ day: d });
  return cells;
});

function hasEvent(day) {
  return eventosDelAnyo.value.some(
    (e) => parseLocalDate(e.appointmentDay)?.getDate() === day,
  );
}

function isPastDay(day) {
  const cellDate = new Date(viewYear.value, viewMonth.value, day);
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return cellDate < todayStart;
}

function isPastEvent(evento) {
  const ed = parseLocalDate(evento.appointmentDay);
  if (!ed) return false;
  const eventDayStart = new Date(ed.getFullYear(), ed.getMonth(), ed.getDate());
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return eventDayStart < todayStart;
}

function isToday(day) {
  return (
    day === today.getDate() &&
    viewMonth.value === today.getMonth() &&
    viewYear.value === today.getFullYear()
  );
}

function dayClass(cell) {
  const base =
    "aspect-square rounded-full flex items-center justify-center text-sm sm:text-base font-medium transition-all";
  const hasEv = hasEvent(cell.day);
  const todayCell = isToday(cell.day);
  const past = isPastDay(cell.day);
  if (hasEv && todayCell) return `${base} bg-bochas-rojo/60 text-white font-bold ring-2 ring-bochas-rojo/60`;
  if (hasEv && past) return `${base} calendar-day-past-event`;
  if (hasEv) return `${base} bg-bochas-rojo text-white font-bold`;
  if (todayCell) return `${base} text-white font-bold bg-white/5`;
  if (past) return `${base} text-gray-600`;
  return `${base} text-gray-300 hover:bg-white/10`;
}

function selectDay(day) {
  selectedDay.value = selectedDay.value === day ? null : day;
}

function prevMonth() {
  if (viewMonth.value === 0) {
    viewMonth.value = 11;
    viewYear.value -= 1;
  } else {
    viewMonth.value -= 1;
  }
  selectedDay.value = null;
}

function nextMonth() {
  if (viewMonth.value === 11) {
    viewMonth.value = 0;
    viewYear.value += 1;
  } else {
    viewMonth.value += 1;
  }
  selectedDay.value = null;
}

function goToday() {
  viewYear.value = today.getFullYear();
  viewMonth.value = today.getMonth();
  selectedDay.value = null;
}

function openModal(evento) {
  modalEvento.value = evento;
  document.body.style.overflow = "hidden";
}
function closeModal() {
  modalEvento.value = null;
  document.body.style.overflow = "";
}

const modalDayNum = computed(() => {
  if (!modalEvento.value) return "";
  return parseLocalDate(modalEvento.value.appointmentDay)?.getDate() || "";
});
const modalMonthShort = computed(() => {
  if (!modalEvento.value) return "";
  const date = parseLocalDate(modalEvento.value.appointmentDay);
  if (!date) return "";
  return date.toLocaleDateString("es-AR", { month: "short" }).replace(".", "");
});

function dayNum(evento) {
  return parseLocalDate(evento.appointmentDay)?.getDate() || "";
}
function monthShort(evento) {
  const date = parseLocalDate(evento.appointmentDay);
  if (!date) return "";
  return date.toLocaleDateString("es-AR", { month: "short" }).replace(".", "");
}
function formatTime(t) {
  if (!t) return "";
  const str = String(t);
  // "HH:MM:SS" -> "HH:MM"
  if (str.length >= 5 && str[2] === ":") return str.slice(0, 5);
  return str;
}

function showTime(evento) {
  const time = String(evento?.appointmentTime || "").trim();
  return !!time && !["00:00", "00:00:00"].includes(time);
}

// Datos formateados para la lista. Apuntamos al computed de mes
// actual + mes siguiente (en vez de `eventosDelMes` que solo tenía
// el mes actual). La grilla del calendario sigue usando `eventosDelAnyo`.
const eventosList = computed(() => {
  if (!props.compact) return eventosDelMesYProximo.value;

  const currentEvents = eventosDelMesYProximo.value.filter(
    (ev) => !ev._isNextMonth,
  );
  const currentGroupCount = dateGroupCount(currentEvents);

  if (currentGroupCount >= 2) return currentEvents;

  const maxGroups = 3;
  const allowedNextDates = new Set();
  let totalGroups = currentGroupCount;

  for (const ev of eventosDelMesYProximo.value) {
    if (!ev._isNextMonth) continue;

    const key = ev._dateKey || eventDateKey(ev);
    if (!allowedNextDates.has(key)) {
      if (totalGroups >= maxGroups) break;
      allowedNextDates.add(key);
      totalGroups += 1;
    }
  }

  return eventosDelMesYProximo.value.filter(
    (ev) => !ev._isNextMonth || allowedNextDates.has(ev._dateKey),
  );
});

defineExpose({
  eventosDelMes,
  eventosFiltrados,
});
</script>

<style scoped>
.calendario-events-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.24) transparent;
}

.calendario-events-scroll::-webkit-scrollbar {
  width: 6px;
}

.calendario-events-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.calendario-events-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.22);
  border-radius: 999px;
}

.calendario-events-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.36);
}

.calendar-event-title {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

.calendar-day-past-event {
  background: rgba(165, 33, 33, 0.22);
  color: rgba(255, 255, 255, 0.78);
  font-weight: 700;
  box-shadow: inset 0 0 0 1px rgba(165, 33, 33, 0.48);
}

</style>
